import Box from '@mui/material/Box';
import { type CSSProperties, useEffect, useState } from 'react';
import { getWebGPUSupport } from 'shaders/react';
import { useSettings } from '@erikdakoda/ui/hooks/useSettings';
import BackMidnightFlowing from './BackMidnightFlowing';
import BackMidnightStatic from './BackMidnightStatic';
import BackHalftoneFlowing from './BackHalftoneFlowing';
import { useLayoutSettings } from '../../hooks/useLayoutSettings';

/**
 * Returns whether shader backgrounds can run (WebGPU adapter available).
 * shaders v3 is WebGPU-only — no WebGL fallback.
 */
async function detectGpuSupport(): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  const support = await getWebGPUSupport();
  return support.supported;
}

export default function Background() {
  'use memo';

  const { settings } = useLayoutSettings();

  const { readSetting, isLoading } = useSettings();

  const [gpuSupported, setGpuSupported] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    void detectGpuSupport().then((supported) => {
      if (!cancelled) {
        setGpuSupported(supported);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const defaultStyle: CSSProperties = {
    fontSize: 0,
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: -1,
  };

  if (isLoading || gpuSupported === null) {
    return null;
  }

  const theme = gpuSupported
    ? readSetting<string>('theme', { allowAnonymous: true }) || settings.backTheme
    : '';

  return (
    <Box className={'background'} style={{ ...defaultStyle }}>
      {theme === 'midnight-flowing' ? (
        <BackMidnightFlowing color={settings.backColor} />
      ) : theme === 'halftone-flowing' ? (
        <BackHalftoneFlowing color={settings.backColor} />
      ) : (
        <BackMidnightStatic color={settings.backColor} />
      )}
    </Box>
  );
}
